import { useEffect, useMemo, useRef, useState } from "react";
import { ConfirmDialog } from "../molecules/ConfirmDialog/index.js";
import type {
  PlayerAICanvasDeliveryBinding,
  PlayerAICanvasDeliveryStatus,
} from "./playerai.types.js";

type DeliveryState = "applied" | "pending" | "failed";

export function useCanvasDelivery(
  binding: PlayerAICanvasDeliveryBinding | undefined,
) {
  const [statuses, setStatuses] = useState<
    Record<string, PlayerAICanvasDeliveryStatus>
  >({});
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<{
    commandId: string;
    earlierReference: string | null;
  } | null>(null);
  const commandKey =
    binding?.commands.map((entry) => entry.commandId).join(",") ?? "";
  const bindingRef = useRef(binding);
  useEffect(() => {
    bindingRef.current = binding;
  }, [binding]);
  const commands = binding?.commands;

  const pendingIds = useMemo(
    () =>
      (commands ?? [])
        .filter((entry) => {
          const status = statuses[entry.commandId];
          return (
            (status === undefined && entry.initialState === "failed") ||
            (status?.outcome.state ?? entry.initialState) === "pending"
          );
        })
        .map((entry) => entry.commandId),
    [commands, statuses],
  );
  const pendingKey = pendingIds.join(",");

  useEffect(() => {
    if (pendingKey.length === 0) return;
    const ids = pendingKey.split(",");
    let cancelled = false;
    const refresh = async () => {
      const currentBinding = bindingRef.current;
      if (!currentBinding) return;
      try {
        const rows = await Promise.all(
          ids.map((id) => currentBinding.getStatus(id)),
        );
        const blockerIds = rows
          .map((row) => row.blockedByCommandId)
          .filter((id): id is string => typeof id === "string");
        const blockers = await Promise.all(
          [...new Set(blockerIds)].map((id) => currentBinding.getStatus(id)),
        );
        if (!cancelled) {
          setStatuses((current) =>
            Object.fromEntries(
              [...Object.values(current), ...rows, ...blockers].map((row) => [
                row.commandId,
                row,
              ]),
            ),
          );
          setError(false);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 3_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [commandKey, pendingKey]);

  const states = (commands ?? []).map(
    (entry) => statuses[entry.commandId]?.outcome.state ?? entry.initialState,
  );
  const state: DeliveryState | undefined =
    states.length === 0
      ? undefined
      : states.includes("failed")
        ? "failed"
        : states.includes("pending")
          ? "pending"
          : "applied";
  const focus =
    (commands ?? []).find(
      (entry) =>
        (statuses[entry.commandId]?.outcome.state ?? entry.initialState) ===
        "failed",
    ) ??
    (commands ?? []).find(
      (entry) =>
        (statuses[entry.commandId]?.outcome.state ?? entry.initialState) ===
        "pending",
    );
  const ownStatus = focus ? statuses[focus.commandId] : undefined;
  const blocker = ownStatus?.blockedByCommandId
    ? statuses[ownStatus.blockedByCommandId]
    : undefined;
  const actionable = blocker ?? ownStatus;
  const actsOnEarlierChange =
    blocker !== undefined && actionable?.commandId === blocker.commandId;
  const earlierReference = blocker?.commandId.slice(0, 8);
  const retryId = actionable?.commandId;
  const canRetry =
    binding?.canRepair === true &&
    retryId !== undefined &&
    actionable?.canRetry === true;
  const discardId =
    binding?.canRepair === true && actionable?.canDiscard
      ? actionable.commandId
      : null;

  const refreshOne = async (commandId: string) => {
    if (!binding) return;
    try {
      const row = await binding.getStatus(commandId);
      const blocker = row.blockedByCommandId
        ? await binding.getStatus(row.blockedByCommandId)
        : null;
      setStatuses((current) => ({
        ...current,
        [row.commandId]: row,
        ...(blocker ? { [blocker.commandId]: blocker } : {}),
      }));
      setError(false);
    } catch {
      setError(true);
    }
  };
  const runAction = async (commandId: string, action: "retry" | "discard") => {
    if (!binding || busy) return;
    setBusy(true);
    try {
      const row = await binding[action](commandId);
      setStatuses((current) => ({ ...current, [row.commandId]: row }));
      if (focus?.commandId && focus.commandId !== commandId)
        await refreshOne(focus.commandId);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
      setConfirmTarget(null);
    }
  };

  return {
    state,
    notice:
      binding && state !== undefined && state !== "applied" ? (
        <div
          className="mt-2 rounded-md border border-pb-amber/40 bg-pb-amber-soft px-2.5 py-2 text-dense-1 text-pb-text-secondary"
          role="status"
        >
          {state === "failed" ? (
            <p>The change was saved, but it did not reach the canvas.</p>
          ) : blocker?.status === "failed" ? (
            <p>
              Waiting on an earlier canvas change that failed ( ref{" "}
              {earlierReference}). Retry or discard that earlier change to
              continue.
            </p>
          ) : ownStatus?.blockedByCommandId ? (
            <p>
              Waiting on an earlier canvas change ( ref{" "}
              {ownStatus.blockedByCommandId.slice(0, 8)}).
              {blocker?.nextAttemptAt
                ? ` Retry is available after ${new Date(blocker.nextAttemptAt).toLocaleString()}.`
                : ""}
            </p>
          ) : (
            <p>The change was saved and is waiting to appear on the canvas.</p>
          )}
          {actionable?.status === "abandoned" ? (
            <p>
              {actsOnEarlierChange
                ? "The earlier canvas change was discarded; its saved change remains."
                : "This canvas update was discarded; the saved change remains."}
            </p>
          ) : null}
          {error ? <p>Could not check canvas delivery. Try again.</p> : null}
          <div className="mt-2 flex flex-wrap gap-2">
            {retryId && canRetry ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => void runAction(retryId, "retry")}
                className="rounded border border-pb-control-border bg-pb-card px-2 py-1"
              >
                Retry delivery
              </button>
            ) : null}
            {discardId ? (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  setConfirmTarget({
                    commandId: discardId,
                    earlierReference: actsOnEarlierChange
                      ? (earlierReference ?? null)
                      : null,
                  })
                }
                className="rounded border border-pb-red/40 bg-pb-card px-2 py-1 text-pb-red-text"
              >
                {actsOnEarlierChange
                  ? "Discard earlier canvas change"
                  : "Discard canvas update"}
              </button>
            ) : null}
            {focus ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => void refreshOne(focus.commandId)}
                className="rounded border border-pb-control-border bg-pb-card px-2 py-1"
              >
                Check status
              </button>
            ) : null}
          </div>
          <ConfirmDialog
            open={confirmTarget !== null}
            title={
              confirmTarget?.earlierReference
                ? `Discard the earlier canvas change (ref ${confirmTarget.earlierReference})?`
                : "Discard this canvas update?"
            }
            confirmLabel="Discard update"
            confirmDisabled={busy}
            cancelDisabled={busy}
            onClose={() => setConfirmTarget(null)}
            onConfirm={() => {
              if (confirmTarget)
                void runAction(confirmTarget.commandId, "discard");
            }}
          >
            {confirmTarget?.earlierReference
              ? "This discards the earlier canvas change, not the update on this card. Its saved widget change remains, but its canvas update will be marked not delivered so later updates can proceed."
              : "The saved widget change stays in place, but this canvas update will be marked not delivered and later updates can proceed."}
          </ConfirmDialog>
        </div>
      ) : null,
  };
}
